import { useTranslation } from "react-i18next";

import { deleteBorder } from "@dashboard/shared/styles";
import type { DraggableProvided, DraggableStateSnapshot } from "@hello-pangea/dnd";
import { Box, Typography } from "@mui/material";
import { DangerOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { ExpandToggleButton } from "@shared/ExpandToggleButton";
import { HoverRow } from "@shared/table/HoverRow";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";

import { useStyles } from "./PolicyCommandItem.style";

type Props = {
    index: number;
    commandLabels: { primary: string; secondary?: string | null };
    open: number;
    onToggleOpen: () => void;
    readOnly: boolean;
    loading: boolean;
    onEdit: () => void;
    onDeleteClick: () => void;
    draggableProvided?: DraggableProvided;
    snapshot?: DraggableStateSnapshot;
};

export const PolicyCommandRow = ({
    index,
    commandLabels,
    open,
    onToggleOpen,
    readOnly,
    loading,
    onEdit,
    onDeleteClick,
    draggableProvided,
    snapshot,
}: Props) => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation(["sidepanels"]);

    return (
        <HoverRow
            key={index}
            ref={draggableProvided?.innerRef}
            rowProps={draggableProvided?.draggableProps}
            clickable={false}
            style={
                snapshot?.isDragging
                    ? { ...draggableProvided?.draggableProps.style, background: "rgba(245,245,245, 0.75)" }
                    : draggableProvided?.draggableProps.style
            }
        >
            <MinimalTableCell align="left" style={deleteBorder(index, open)}>
                <Box style={{ display: "flex", alignItems: "center" }}>
                    <ExpandToggleButton expanded={open === index} onClick={onToggleOpen} className={classes.collapseBtn} />
                    {!readOnly && draggableProvided ? (
                        <div {...draggableProvided.dragHandleProps}>
                            <i className={cx("fa-solid fa-grip-vertical", classes.dragHandleIcon)} />
                        </div>
                    ) : null}
                </Box>
            </MinimalTableCell>
            <MinimalTableCell align="center" width={20} style={deleteBorder(index, open)}>
                <Typography>{index + 1}</Typography>
            </MinimalTableCell>
            <MinimalTableCell align="left" style={{ ...deleteBorder(index, open), height: "auto", whiteSpace: "normal" }}>
                <Typography noWrap>{commandLabels.primary}</Typography>
                {commandLabels.secondary ? (
                    <Typography variant="caption" color="textSecondary" display="block" noWrap>
                        {commandLabels.secondary}
                    </Typography>
                ) : null}
            </MinimalTableCell>
            <MinimalTableCell align="right" style={deleteBorder(index, open)}>
                {!readOnly ? (
                    <div className={classes.buttonBox}>
                        <SecondaryButton
                            tooltipProps={{ title: t("sidepanels:groupPanel.tooltip.edit") }}
                            onClick={onEdit}
                            className={loading ? classes.iconDisabled : ""}
                            loading={loading}
                            disabled={loading}
                        >
                            <i className="fa-solid fa-fw fa-pencil" />
                        </SecondaryButton>
                        <DangerOutlinedButton
                            onClick={onDeleteClick}
                            tooltipProps={{ title: t("sidepanels:groupPanel.tooltip.delete") }}
                            loading={loading}
                            className={loading ? classes.iconDisabled : ""}
                            disabled={loading}
                        >
                            <i className="fa-solid fa-fw fa-trash" />
                        </DangerOutlinedButton>
                    </div>
                ) : null}
            </MinimalTableCell>
        </HoverRow>
    );
};
